import { createRouter, createWebHistory, type RouteLocationNormalized } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { tr } from '@/i18n'

declare module 'vue-router' {
  interface RouteMeta {
    /** Reachable without signing in (sign-in, password pages). */
    public?: boolean
    /** Part of the MFA step after the password. */
    mfa?: boolean
    /** Permission the page needs (UI hint; the API enforces it). */
    permission?: string
    title?: string
  }
}

export const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/auth/LoginView.vue'),
      meta: { public: true, title: 'Sign in' },
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: () => import('@/views/auth/ForgotPasswordView.vue'),
      meta: { public: true, title: 'Reset your password' },
    },
    {
      path: '/set-password',
      name: 'set-password',
      component: () => import('@/views/auth/SetPasswordView.vue'),
      props: { purpose: 'invite' },
      meta: { public: true, title: 'Set your password' },
    },
    {
      path: '/reset-password',
      name: 'reset-password',
      component: () => import('@/views/auth/SetPasswordView.vue'),
      props: { purpose: 'reset' },
      meta: { public: true, title: 'Choose a new password' },
    },
    {
      path: '/mfa',
      name: 'mfa',
      component: () => import('@/views/auth/MfaVerifyView.vue'),
      meta: { mfa: true, title: 'Enter your code' },
    },
    {
      path: '/mfa/setup',
      name: 'mfa-setup',
      component: () => import('@/views/auth/MfaSetupView.vue'),
      meta: { mfa: true, title: 'Set up Google Authenticator' },
    },
    {
      path: '/',
      component: () => import('@/layouts/AppLayout.vue'),
      children: [
        {
          path: '',
          name: 'dashboard',
          component: () => import('@/views/DashboardView.vue'),
          meta: { title: 'Dashboard' },
        },
        {
          path: 'orders',
          name: 'orders',
          component: () => import('@/views/orders/OrdersView.vue'),
          meta: { permission: 'orders.read', title: 'Orders' },
        },
        {
          path: 'orders/new',
          name: 'order-new',
          component: () => import('@/views/orders/NewOrderView.vue'),
          meta: { permission: 'orders.create', title: 'Book an order' },
        },
        {
          path: 'orders/:id',
          name: 'order',
          component: () => import('@/views/orders/OrderDetailView.vue'),
          props: true,
          meta: { permission: 'orders.read', title: 'Order' },
        },
        {
          path: 'packing',
          name: 'packing',
          component: () => import('@/views/shipping/PackingView.vue'),
          meta: { permission: 'orders.fulfill', title: 'Packing' },
        },
        {
          path: 'parcels',
          name: 'parcels',
          component: () => import('@/views/shipping/ParcelsView.vue'),
          meta: { permission: 'orders.read', title: 'Parcels' },
        },
        {
          path: 'returns',
          name: 'returns',
          component: () => import('@/views/returns/ReturnsView.vue'),
          meta: { permission: 'returns.manage', title: 'Returns' },
        },
        {
          path: 'returns/:id',
          name: 'return',
          component: () => import('@/views/returns/ReturnDetailView.vue'),
          props: true,
          meta: { permission: 'returns.manage', title: 'Return' },
        },
        {
          path: 'products',
          name: 'products',
          component: () => import('@/views/catalog/ProductsView.vue'),
          meta: { permission: 'products.read', title: 'Products' },
        },
        {
          path: 'products/:id',
          name: 'product',
          component: () => import('@/views/catalog/ProductEditorView.vue'),
          props: true,
          meta: { permission: 'products.read', title: 'Product' },
        },
        {
          path: 'categories',
          name: 'categories',
          component: () => import('@/views/catalog/CategoriesView.vue'),
          meta: { permission: 'products.read', title: 'Categories' },
        },
        {
          path: 'sales',
          name: 'sales',
          component: () => import('@/views/sales/SalesView.vue'),
          meta: { permission: 'products.read', title: 'Sales' },
        },
        {
          path: 'sales/:id',
          name: 'sale',
          component: () => import('@/views/sales/SaleEditorView.vue'),
          props: true,
          meta: { permission: 'products.read', title: 'Sale' },
        },
        {
          path: 'stock',
          name: 'stock',
          component: () => import('@/views/stock/StockView.vue'),
          meta: { permission: 'inventory.read', title: 'Stock' },
        },
        {
          path: 'stock/history',
          name: 'stock-history',
          component: () => import('@/views/stock/StockHistoryView.vue'),
          meta: { permission: 'inventory.read', title: 'Stock history' },
        },
        {
          path: 'purchases',
          name: 'purchases',
          component: () => import('@/views/stock/PurchasesView.vue'),
          meta: { permission: 'inventory.read', title: 'Purchases' },
        },
        {
          path: 'purchases/:id',
          name: 'purchase',
          component: () => import('@/views/stock/PurchaseEditorView.vue'),
          props: true,
          meta: { permission: 'inventory.read', title: 'Purchase' },
        },
        {
          path: 'suppliers',
          name: 'suppliers',
          component: () => import('@/views/stock/SuppliersView.vue'),
          meta: { permission: 'inventory.read', title: 'Suppliers' },
        },
        {
          path: 'invoices',
          name: 'invoices',
          component: () => import('@/views/finance/InvoicesView.vue'),
          meta: { permission: 'finance.read', title: 'Invoices' },
        },
        {
          path: 'expenses',
          name: 'expenses',
          component: () => import('@/views/finance/ExpensesView.vue'),
          meta: { permission: 'finance.read', title: 'Expenses' },
        },
        {
          path: 'salaries',
          name: 'salaries',
          component: () => import('@/views/finance/SalariesView.vue'),
          meta: { permission: 'finance.read', title: 'Salaries' },
        },
        {
          path: 'capital',
          name: 'capital',
          component: () => import('@/views/finance/CapitalView.vue'),
          meta: { permission: 'finance.read', title: 'Capital' },
        },
        {
          path: 'reports/performance',
          name: 'report-performance',
          component: () => import('@/views/reports/PerformanceView.vue'),
          meta: { permission: 'finance.read', title: 'Profit' },
        },
        {
          path: 'reports/products',
          name: 'report-products',
          component: () => import('@/views/reports/ProductsReportView.vue'),
          meta: { permission: 'finance.read', title: 'Product performance' },
        },
        {
          path: 'reports/stock-value',
          name: 'report-stock-value',
          component: () => import('@/views/reports/StockValueView.vue'),
          meta: { permission: 'finance.read', title: 'Stock value' },
        },
        {
          path: 'reports/suppliers',
          name: 'report-suppliers',
          component: () => import('@/views/reports/SuppliersReportView.vue'),
          meta: { permission: 'finance.read', title: 'Purchases by supplier' },
        },
        {
          path: 'reports/gst',
          name: 'report-gst',
          component: () => import('@/views/reports/GstReturnsView.vue'),
          meta: { permission: 'finance.read', title: 'GST returns' },
        },
        {
          path: 'settings/test-data',
          name: 'settings-test-data',
          component: () => import('@/views/settings/TestDataView.vue'),
          meta: { permission: 'test-data.manage', title: 'Test data' },
        },
        {
          path: 'settings/outbox',
          name: 'settings-outbox',
          component: () => import('@/views/settings/OutboxView.vue'),
          meta: { permission: 'settings.manage', title: 'Outbox' },
        },
        {
          path: 'settings/gst',
          name: 'settings-gst',
          component: () => import('@/views/settings/GstSettingsView.vue'),
          meta: { permission: 'settings.manage', title: 'GST' },
        },
        {
          path: 'staff',
          name: 'staff',
          component: () => import('@/views/staff/StaffListView.vue'),
          meta: { permission: 'users.manage', title: 'Staff' },
        },
        {
          path: 'roles',
          name: 'roles',
          component: () => import('@/views/roles/RolesView.vue'),
          meta: { permission: 'roles.manage', title: 'Roles' },
        },
        {
          path: 'audit-log',
          name: 'audit-log',
          component: () => import('@/views/audit/AuditLogView.vue'),
          meta: { permission: 'audit.read', title: 'Audit log' },
        },
      ],
    },
    { path: '/:pathMatch(.*)*', redirect: '/' },
  ],
})

/** Where a visitor should be, given their session. Exported for tests. */
export function resolveAccess(to: RouteLocationNormalized, auth: ReturnType<typeof useAuthStore>) {
  if (to.meta.public) {
    return auth.isSignedIn && to.name === 'login' ? { name: 'dashboard' } : true
  }

  if (to.meta.mfa) {
    if (auth.isSignedIn) return { name: 'dashboard' }
    if (!auth.pendingMfa) return { name: 'login' }
    const wanted = auth.pendingMfa === 'enrollment_required' ? 'mfa-setup' : 'mfa'
    return to.name === wanted ? true : { name: wanted, query: to.query }
  }

  if (!auth.isSignedIn) {
    const redirect = to.fullPath === '/' ? undefined : to.fullPath
    if (auth.pendingMfa)
      return { name: auth.pendingMfa === 'enrollment_required' ? 'mfa-setup' : 'mfa', query: { redirect } }
    return { name: 'login', query: { redirect } }
  }

  if (to.meta.permission && !auth.can(to.meta.permission)) return { name: 'dashboard' }

  return true
}

router.beforeEach(async (to) => {
  const auth = useAuthStore()
  await auth.load()
  return resolveAccess(to, auth)
})

router.afterEach((to) => {
  document.title = to.meta.title ? `${tr(String(to.meta.title))} · ${tr('Admin')}` : tr('Admin')
})
